import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useFleetStore from '../stores/useFleetStore';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import Checkbox from '../components/ui/Checkbox';
import { Field } from '../components/ui/Field';
import { Select } from '../components/ui/Select';
import Panel from '../components/ui/Panel';
import EmptyState from '../components/ui/EmptyState';
import LogoSpinner from '../components/ui/LogoSpinner';
import Reveal from '../components/ui/Reveal';
import { printStandardLabels, printSquareQRLabels, printBothLabelTypes } from '../utils/printing-utils';

// Icons
const PrinterIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1"/>
        <path d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v-1a2 2 0 0 0-2-2zm7 2v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1"/>
    </svg>
);

const CartIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5M5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4m7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4m-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2m7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2"/>
    </svg>
);

const BatchPrint = () => {
    const api = useApi();
    const toast = useToast();
    const drones = useFleetStore((state) => state.drones);
    const [batchDetailsArray, setBatchDetailsArray] = useState([]);
    const [selectedBatchNumber, setSelectedBatchNumber] = useState('');
    const [selectedModel, setSelectedModel] = useState('all');
    const [selectedDofM, setSelectedDofM] = useState('all');
    const [serialNumbers, setSerialNumbers] = useState([]);
    const [filteredSerials, setFilteredSerials] = useState([]);
    const [availableModels, setAvailableModels] = useState([]);
    const [availableDofMs, setAvailableDofMs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [includeFAA, setIncludeFAA] = useState(false);

    // Last X drones state
    const [lastXCount, setLastXCount] = useState(100);
    const [lastXSerials, setLastXSerials] = useState([]);
    const [loadingLastX, setLoadingLastX] = useState(false);

    // Cart state from localStorage
    const [cart, setCart] = useState(() => {
        try {
            const raw = localStorage.getItem("droneCart");
            return raw ? new Set(JSON.parse(raw)) : new Set();
        } catch {
            return new Set();
        }
    });

    // Fetch batch details on mount
    useEffect(() => {
        const fetchBatchDetails = async () => {
            await api.get(`/batchdetails`, {
                onSuccess: (batchesData) => {
                    const reversedBatches = batchesData.reverse();
                    setBatchDetailsArray(reversedBatches);
                    if (reversedBatches.length > 0) {
                        setSelectedBatchNumber(reversedBatches[0].batchserial.toString().padStart(4, '0'));
                    }
                },
                errorAlert: 'Batches could not be found, please try again'
            });
        };

        fetchBatchDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch serial numbers for selected batch
    const fetchSerialNumbersForBatch = async () => {
        if (!selectedBatchNumber) {
            toast.warning('Please select a batch');
            return;
        }

        setLoading(true);
        await api.get(`/serialnumbers`, {
            onSuccess: (allSerials) => {
                // Filter by selected batch and sort by dronereadable
                const batchSerials = allSerials
                    .filter(serial => serial.batchserial === selectedBatchNumber)
                    .sort((a, b) => parseInt(a.dronereadable) - parseInt(b.dronereadable));

                setSerialNumbers(batchSerials);

                // Extract unique models and DofMs for filtering
                const models = [...new Set(batchSerials.map(s => s.modelserial))].sort();
                const dofms = [...new Set(batchSerials.map(s => s.dofmserial))].sort();

                setAvailableModels(models);
                setAvailableDofMs(dofms);
                setSelectedModel('all');
                setSelectedDofM('all');
                setFilteredSerials(batchSerials);
                setLoading(false);
            },
            errorAlert: 'Serial numbers could not be found, please try again'
        });
        setLoading(false);
    };

    // Apply filters whenever model or DofM changes
    useEffect(() => {
        if (serialNumbers.length === 0) return;

        let filtered = serialNumbers;

        if (selectedModel !== 'all') {
            filtered = filtered.filter(s => s.modelserial === selectedModel);
        }

        if (selectedDofM !== 'all') {
            filtered = filtered.filter(s => s.dofmserial === selectedDofM);
        }

        setFilteredSerials(filtered);
    }, [selectedModel, selectedDofM, serialNumbers]);

    // Helper to lookup FAA registration for a serial number
    const getFAARegistration = (serialnumber) => {
        if (!includeFAA || !drones) return null;
        const drone = drones.find(d => d.serialnumber === serialnumber);
        return drone?.faa_reg || null;
    };

    // Print handlers using shared utilities
    const handlePrintStandardLabels = async (serialsToPrint) => {
        if (serialsToPrint.length === 0) {
            toast.warning('No serial numbers to print');
            return;
        }
        await printStandardLabels(serialsToPrint, {
            includeFAA,
            getFAARegistration
        });
    };

    const handlePrintSquareQRLabels = async (serialsToPrint) => {
        if (serialsToPrint.length === 0) {
            toast.warning('No serial numbers to print');
            return;
        }
        await printSquareQRLabels(serialsToPrint);
    };

    const handlePrintBothLabels = async (serialsToPrint) => {
        if (serialsToPrint.length === 0) {
            toast.warning('No serial numbers to print');
            return;
        }
        await printBothLabelTypes(serialsToPrint, {
            includeFAA,
            getFAARegistration
        });
    };

    // Print labels for filtered batch drones
    const printBatchLabels = () => {
        handlePrintStandardLabels(filteredSerials);
    };

    const printBatchQRLabels = () => {
        handlePrintSquareQRLabels(filteredSerials);
    };

    const printBatchBothLabels = () => {
        handlePrintBothLabels(filteredSerials);
    };

    // Get cart serials (helper for cart printing)
    const getCartSerials = async () => {
        if (cart.size === 0) {
            toast.warning('Cart is empty. Add drones from the fleet page first.');
            return null;
        }

        let cartSerials = null;
        await api.get(`/serialnumbers`, {
            onSuccess: (allSerials) => {
                cartSerials = allSerials
                    .filter(serial => cart.has(serial.serialnumber))
                    .sort((a, b) => parseInt(a.dronereadable) - parseInt(b.dronereadable));

                if (cartSerials.length === 0) {
                    toast.warning('No matching serial numbers found for cart items');
                    cartSerials = null;
                }
            }
        });
        return cartSerials;
    };

    // Print labels from cart
    const printCartLabels = async () => {
        const cartSerials = await getCartSerials();
        if (cartSerials) handlePrintStandardLabels(cartSerials);
    };

    const printCartQRLabels = async () => {
        const cartSerials = await getCartSerials();
        if (cartSerials) handlePrintSquareQRLabels(cartSerials);
    };

    const printCartBothLabels = async () => {
        const cartSerials = await getCartSerials();
        if (cartSerials) handlePrintBothLabels(cartSerials);
    };

    // Clear cart
    const clearCart = () => {
        setCart(new Set());
        localStorage.setItem("droneCart", JSON.stringify([]));
    };

    // Fetch last X drones by dronereadable
    const fetchLastXDrones = async () => {
        if (lastXCount < 1) {
            toast.warning('Please enter a valid number');
            return;
        }

        setLoadingLastX(true);
        await api.get(`/serialnumbers`, {
            onSuccess: (allSerials) => {
                // Filter to only assigned serials (drones that exist)
                // Sort by dronereadable descending, take last X
                const sortedSerials = allSerials
                    .filter(serial => serial.assigned && serial.dronereadable)
                    .sort((a, b) => parseInt(b.dronereadable, 10) - parseInt(a.dronereadable, 10))
                    .slice(0, lastXCount);

                // Reverse to show in ascending order (oldest to newest in the last X)
                setLastXSerials(sortedSerials.reverse());
                setLoadingLastX(false);
            },
            errorAlert: 'Serial numbers could not be found, please try again'
        });
        setLoadingLastX(false);
    };

    // Print last X drones labels
    const printLastXLabels = () => {
        if (lastXSerials.length === 0) {
            toast.warning('No drones loaded. Click "Load" first.');
            return;
        }
        handlePrintStandardLabels(lastXSerials);
    };

    const printLastXQRLabels = () => {
        if (lastXSerials.length === 0) {
            toast.warning('No drones loaded. Click "Load" first.');
            return;
        }
        handlePrintSquareQRLabels(lastXSerials);
    };

    const printLastXBothLabels = () => {
        if (lastXSerials.length === 0) {
            toast.warning('No drones loaded. Click "Load" first.');
            return;
        }
        handlePrintBothLabels(lastXSerials);
    };

    const batchOptions = batchDetailsArray.map(batch => {
        const key = batch.batchserial.toString().padStart(4, '0');
        return { key, label: `Batch ${key}` };
    });

    return (
        <PageLayout
            kind="focus"
            className="pt-4"
            header={
            <div className="mb-6">
                <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-primary/20 flex items-center justify-center text-primary-ink">
                            <PrinterIcon />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-white">Batch Label Print</h1>
                            <p className="text-on-surface-variant text-sm">Print serial number labels for drones</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Link
                            to="/fleet"
                            className="px-3 py-2 text-sm font-medium bg-surface-container-highest text-on-surface rounded
                                border border-transparent transition-all duration-200
                                hover:text-white hover:border-outline"
                        >
                            Fleet
                        </Link>
                        <Link
                            to="/"
                            className="px-3 py-2 text-sm font-medium bg-surface-container-highest text-on-surface rounded
                                border border-transparent transition-all duration-200
                                hover:text-white hover:border-outline"
                        >
                            Home
                        </Link>
                    </div>
                </div>
            </div>
            }
        >
            {/* Print Options */}
            <Panel title="Print Options" className="mb-6">

                {/* FAA checkbox for standard labels */}
                <Checkbox
                    checked={includeFAA}
                    onChange={(e) => setIncludeFAA(e.target.checked)}
                    label="Include FAA registrations on standard labels"
                />

                {/* Print button legend */}
                <div className="mt-4 pt-4 border-t border-gray-700">
                    <p className="text-xs font-medium text-on-surface-variant mb-2">Print Buttons:</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-on-surface-variant">
                        <div className="flex items-center gap-2">
                            <span className="inline-block w-3 h-3 bg-primary rounded"></span>
                            <span><strong>Labels</strong> = 1.5" × 0.375" text</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="inline-block w-3 h-3 bg-blue-600 rounded"></span>
                            <span><strong>QR</strong> = 11.4mm square (QR + serial)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="inline-block w-3 h-3 bg-green-600 rounded"></span>
                            <span><strong>Both</strong> = print to both printers</span>
                        </div>
                    </div>
                </div>
            </Panel>

            {/* Cart Section */}
            <Reveal show={cart.size > 0}>
                <div className="bg-primary/10 rounded border-2 border-primary/30 p-4 mb-6">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded bg-primary/20 flex items-center justify-center text-primary-ink">
                                <CartIcon />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-white">Cart</h2>
                                <p className="text-xs text-on-surface-variant">{cart.size} drone{cart.size !== 1 ? 's' : ''} selected</p>
                            </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <button
                                className={buttonClass('filled')}
                                onClick={printCartLabels}
                            >
                                Labels
                            </button>
                            <button
                                className={buttonClass('info')}
                                onClick={printCartQRLabels}
                            >
                                QR
                            </button>
                            <button
                                className={buttonClass('good')}
                                onClick={printCartBothLabels}
                            >
                                Both
                            </button>
                            <button
                                className={buttonClass('danger')}
                                onClick={clearCart}
                            >
                                Clear
                            </button>
                        </div>
                    </div>

                    {/* Cart items preview */}
                    <Panel label="Cart items" padding="sm" className="max-h-28 overflow-y-auto">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs font-mono">
                            {[...cart].map(serial => (
                                <div key={serial} className="text-primary/80 truncate">
                                    {serial}
                                </div>
                            ))}
                        </div>
                    </Panel>
                </div>
            </Reveal>

            {/* Last X Drones Section */}
            <Panel title="Print Last X Drones" className="mb-6">
                <p className="text-xs text-on-surface-variant mb-3">Load the most recently created drones based on drone readable serial number</p>
                <div className="flex gap-3 items-end">
                    <div className="flex-1">
                        <label className="block text-xs font-medium text-on-surface-variant mb-1.5">Number of Drones</label>
                        <Field
                            type="number"
                            min="1"
                            max="1000"
                            value={lastXCount}
                            onChange={(e) => setLastXCount(parseInt(e.target.value) || 0)}
                            placeholder="e.g., 210"
                        />
                    </div>
                    <button
                        className={buttonClass('filled', 'lg')}
                        onClick={fetchLastXDrones}
                        disabled={loadingLastX}
                        aria-busy={loadingLastX}
                    >
                        {loadingLastX ? (
                            <span className="flex items-center gap-2">
                                <LogoSpinner size="inline" />
                                Loading...
                            </span>
                        ) : 'Load'}
                    </button>
                </div>

                {/* Last X Results */}
                <Reveal show={lastXSerials.length > 0}>
                    <div className="mt-4 space-y-3">
                        <Panel label="Loaded drones" on="panel" padding="sm">
                          <div className="flex items-center justify-between">
                            <div>
                                <p className="text-white font-semibold">{lastXSerials.length} drones loaded</p>
                                <p className="text-xs text-on-surface-variant">
                                    Drone #{lastXSerials[0]?.dronereadable} to #{lastXSerials[lastXSerials.length - 1]?.dronereadable}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    className={buttonClass('filled')}
                                    onClick={printLastXLabels}
                                >
                                    Labels
                                </button>
                                <button
                                    className={buttonClass('info')}
                                    onClick={printLastXQRLabels}
                                >
                                    QR
                                </button>
                                <button
                                    className={buttonClass('good')}
                                    onClick={printLastXBothLabels}
                                >
                                    Both
                                </button>
                            </div>
                          </div>
                        </Panel>

                        {/* Collapsible serial list */}
                        <details className="bg-surface-container rounded">
                            <summary className="px-3 py-2 cursor-pointer text-sm text-on-surface-variant hover:text-on-surface">
                                View serial numbers
                            </summary>
                            <div className="p-3 max-h-48 overflow-y-auto border-t border-outline-variant">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                                    {lastXSerials.map(serial => (
                                        <div
                                            key={serial.serialnumber}
                                            className="px-3 py-2 bg-surface-container-high rounded
                                                font-mono text-sm text-on-surface flex justify-between"
                                        >
                                            <span className="truncate">{serial.serialnumber}</span>
                                            <span className="text-on-surface-variant ml-2">#{serial.dronereadable}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </details>
                    </div>
                </Reveal>
            </Panel>

            {/* Batch Print Section */}
            <div className={cart.size > 0 ? "pt-2" : ""}>
                <Reveal show={cart.size > 0}>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex-1 h-px bg-outline-variant" />
                        <span className="text-sm text-on-surface-variant">or print by batch</span>
                        <div className="flex-1 h-px bg-outline-variant" />
                    </div>
                </Reveal>

                {/* Batch Selector */}
                <Panel title="Select Batch" className="mb-4">
                    <div className="flex gap-3">
                        <Select
                            label="Batch"
                            className="flex-1"
                            value={selectedBatchNumber}
                            onChange={setSelectedBatchNumber}
                            options={batchOptions}
                        />
                        <button
                            className={buttonClass('filled', 'lg')}
                            onClick={fetchSerialNumbersForBatch}
                            disabled={loading}
                            aria-busy={loading}
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <LogoSpinner size="inline" />
                                    Loading...
                                </span>
                            ) : 'Load Batch'}
                        </button>
                    </div>
                </Panel>

                {/* Loaded Batch Data */}
                <Reveal show={serialNumbers.length > 0}>
                    <div className="space-y-4">
                        {/* Filters */}
                        <Panel title="Filters">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-on-surface-variant mb-1.5">Model</label>
                                    <Select
                                        label="Model"
                                        size="sm"
                                        value={selectedModel}
                                        onChange={setSelectedModel}
                                        options={[
                                            { key: 'all', label: `All Models (${availableModels.length})` },
                                            ...availableModels.map(model => ({
                                                key: model,
                                                label: `${model} (${serialNumbers.filter(s => s.modelserial === model).length})`,
                                            })),
                                        ]}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-on-surface-variant mb-1.5">Date of Manufacture</label>
                                    <Select
                                        label="Date of manufacture"
                                        size="sm"
                                        value={selectedDofM}
                                        onChange={setSelectedDofM}
                                        options={[
                                            { key: 'all', label: `All Dates (${availableDofMs.length})` },
                                            ...availableDofMs.map(dofm => ({
                                                key: dofm,
                                                label: `${dofm} (${serialNumbers.filter(s => s.dofmserial === dofm).length})`,
                                            })),
                                        ]}
                                    />
                                </div>
                            </div>
                        </Panel>

                        {/* Results Summary & Print Button */}
                        <Panel label="Results">
                          <div className="flex items-center justify-between">
                            <div>
                                <p className="text-white font-semibold">
                                    {filteredSerials.length} of {serialNumbers.length} serials
                                </p>
                                <p className="text-xs text-on-surface-variant">
                                    {selectedModel !== 'all' && `Model: ${selectedModel}`}
                                    {selectedModel !== 'all' && selectedDofM !== 'all' && ' • '}
                                    {selectedDofM !== 'all' && `DofM: ${selectedDofM}`}
                                    {selectedModel === 'all' && selectedDofM === 'all' && 'No filters applied'}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    className={buttonClass('filled')}
                                    onClick={printBatchLabels}
                                    disabled={filteredSerials.length === 0}
                                >
                                    Labels
                                </button>
                                <button
                                    className={buttonClass('info')}
                                    onClick={printBatchQRLabels}
                                    disabled={filteredSerials.length === 0}
                                >
                                    QR
                                </button>
                                <button
                                    className={buttonClass('good')}
                                    onClick={printBatchBothLabels}
                                    disabled={filteredSerials.length === 0}
                                >
                                    Both
                                </button>
                            </div>
                          </div>
                        </Panel>

                        {/* Serial Numbers List */}
                        <Panel
                            title="Serial Numbers"
                            padding="none"
                            className="overflow-hidden"
                            actions={<span className="text-xs text-on-surface-variant">{filteredSerials.length} items</span>}
                        >
                            <div className="p-3 max-h-64 overflow-y-auto">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                                    {filteredSerials.map(serial => (
                                        <div
                                            key={serial.serialnumber}
                                            className="px-3 py-2 bg-surface-container rounded
                                                font-mono text-sm text-on-surface hover:bg-surface-container-high hover:text-primary
                                                transition-all duration-150"
                                        >
                                            {serial.serialnumber}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Panel>
                    </div>
                </Reveal>

                {/* Empty state when no batch loaded */}
                <Reveal show={serialNumbers.length === 0}>
                    <EmptyState variant="panel" title="No batch loaded">
                        Select a batch above and click "Load Batch" to see serial numbers
                    </EmptyState>
                </Reveal>
            </div>
        </PageLayout>
    );
};

export default BatchPrint;
